import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Calendar, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReminderNotifications() {
  const { user, token } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    // Vérifier immédiatement au montage
    fetchReminders();
  }, [user]);

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API_URL}/contracts/notes/reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      if (response.data.length > 0) {
        setReminders(response.data);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const handleMarkAsRead = async (noteId) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/contracts/notes/${noteId}/mark-reminder-shown`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Retirer le rappel de la liste
      setReminders(prev => prev.filter(r => r.id !== noteId));
      toast.success('Rappel marqué comme lu');
      
      // Fermer le modal si plus de rappels
      if (reminders.length === 1) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error marking reminder:', error);
      toast.error('Erreur lors du marquage du rappel');
    } finally {
      setLoading(false);
    }
  };

  const handleSnooze = async (noteId, days) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/contracts/notes/${noteId}/snooze-reminder?days=${days}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Retirer le rappel de la liste
      setReminders(prev => prev.filter(r => r.id !== noteId));
      
      const dateLabel = days === 1 ? 'demain' : `dans ${days} jours`;
      toast.success(`Rappel reporté à ${dateLabel}`);
      
      // Fermer le modal si plus de rappels
      if (reminders.length === 1) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error snoozing reminder:', error);
      toast.error('Erreur lors du report du rappel');
    } finally {
      setLoading(false);
    }
  };

  if (reminders.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='text-xl font-bold flex items-center gap-2'>
            <Bell className='h-6 w-6 text-orange-600 animate-pulse' />
            Rappels du jour ({reminders.length})
          </DialogTitle>
          <p className='text-sm text-muted-foreground mt-1'>
            Vous avez des notes avec rappel pour aujourd'hui
          </p>
        </DialogHeader>

        <div className='space-y-4 mt-4'>
          {reminders.map((reminder) => (
            <div key={reminder.id} className='border rounded-lg p-4 bg-orange-50/50 dark:bg-orange-900/10'>
              <div className='flex items-start justify-between mb-3'>
                <div className='flex items-center gap-2'>
                  <Badge variant='secondary' className='text-xs'>
                    {reminder.category}
                  </Badge>
                  <span className='text-xs text-muted-foreground flex items-center gap-1'>
                    <Calendar className='h-3 w-3' />
                    Aujourd'hui
                  </span>
                </div>
              </div>

              {reminder.contract_info && (
                <div className='mb-2 text-sm font-medium text-blue-600'>
                  <FileText className='h-4 w-4 inline mr-1' />
                  {reminder.contract_info.family_name} {reminder.contract_info.given_name} - {reminder.contract_info.account_number}
                </div>
              )}

              <p className='text-sm whitespace-pre-wrap mb-4 text-foreground'>
                {reminder.content}
              </p>

              <div className='flex flex-wrap gap-2'>
                <Button
                  size='sm'
                  variant='default'
                  onClick={() => handleMarkAsRead(reminder.id)}
                  disabled={loading}
                  className='bg-green-600 hover:bg-green-700'
                >
                  <Check className='h-4 w-4 mr-1' />
                  Lu
                </Button>
                
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => handleSnooze(reminder.id, 1)}
                  disabled={loading}
                >
                  Demain
                </Button>
                
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => handleSnooze(reminder.id, 3)}
                  disabled={loading}
                >
                  Dans 3 jours
                </Button>
                
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => handleSnooze(reminder.id, 7)}
                  disabled={loading}
                >
                  Dans 1 semaine
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className='mt-4 pt-4 border-t'>
          <Button
            variant='ghost'
            onClick={() => setIsOpen(false)}
            className='w-full'
          >
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
